import { ReorderableFormSectionChild, TagGroup, UseModalHook } from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { ConfigureBidGroupsStepQuery } from "src/routes/bids/plan-based-bid-package/components/2.ConfigureBidGroupsStep.generated";
import { AddOptionModal } from "src/routes/bids/plan-based-bid-package/components/AddOptionModal";
import { BidPlanDimensionRows } from "src/routes/bids/plan-based-bid-package/components/BidPlanDimensionRows";
import {
  groupKey,
  PlanBasedBidPackageBidPlanGroup,
  PlanBasedBidPackageFormState,
} from "src/routes/bids/plan-based-bid-package/utils";

type BuildOptionChildSectionParams = {
  formState: ObjectState<PlanBasedBidPackageFormState>;
  group: ObjectState<PlanBasedBidPackageBidPlanGroup>;
  dimensionResults: ConfigureBidGroupsStepQuery["potentialBidPlanDimensions"];
  potentialBidPlanOptions: ConfigureBidGroupsStepQuery["bidPackage"]["potentialBidPlanOptions"];
  openModal: UseModalHook["openModal"];
};

/**
 * Builds one Add-On Option's `childSection`. `id`/`orderField` make this natively draggable —
 * Beam's `FormSection` renders a drag handle and reorders `childSections` on drop by permuting
 * their `orderField` values directly, so no manual `DnDGrid`/`onReorder` wiring is needed here.
 */
export function buildOptionChildSection(params: BuildOptionChildSectionParams): ReorderableFormSectionChild {
  const { formState, group, dimensionResults, potentialBidPlanOptions, openModal } = params;
  const globalOptionId = group.globalOptionId.value;
  const option = potentialBidPlanOptions.find((o) => o.globalOption.id === globalOptionId);
  const planNames =
    option?.readyPlanOptions
      .map((rpo) => rpo.readyPlan)
      .uniqueByKey("id")
      .map((rp) => rp.name) ?? [];

  function openEditModal() {
    openModal({
      content: (
        <AddOptionModal
          formState={formState}
          dimensionResults={dimensionResults}
          potentialBidPlanOptions={potentialBidPlanOptions}
          editingGroup={group}
        />
      ),
    });
  }

  function removeOption() {
    const next = (formState.bidPlanGroups.value ?? []).filter((g) => g.globalOptionId !== globalOptionId);
    formState.bidPlanGroups.set(next);
  }

  return {
    id: groupKey(group.value)!,
    orderField: group.order,
    title: option?.globalOption.name ?? "Option",
    actions: [
      { kind: "icon", icon: "pencil", onClick: openEditModal, label: "Edit Option" },
      { kind: "icon", icon: "trash", onClick: removeOption, label: "Remove Option" },
    ],
    fields: (
      <>
        {planNames.nonEmpty && <TagGroup tags={planNames.map((name) => ({ text: name }))} />}
        <BidPlanDimensionRows group={group} dimensionResults={dimensionResults} />
      </>
    ),
  };
}
